<section
  class="workflow-notification-editor"
  [class.workflow-notification-editor--embedded]="embeddedContext"
  [attr.aria-labelledby]="instanceId + '-title'"
  data-testid="workflow-notification-editor"
>
  <header class="workflow-notification-editor__header">
    <div>
      <h6
        #notificationEditorHeading
        [id]="instanceId + '-title'"
        [class.workflow-notification-editor__visually-hidden]="embeddedContext"
        tabindex="-1"
      >
        {{ embeddedContext ? "Recipients" : "Notifications" }}
      </h6>
      <p *ngIf="!embeddedContext">Choose who to notify {{ triggerLabel }}.</p>
    </div>
    <button
      uiButton
      severity="primary"
      [outlined]="false"
      *ngIf="canAuthor && !inspectionMode && !isAdding()"
      #notificationAddButton
      type="button"
      class="workflow-notification-editor__add"
      [disabled]="interactionBlocked || referenceLoading || formDirty()"
      (click)="beginAdd()"
    >
      Add recipient
    </button>
  </header>

  <p
    *ngIf="ruleEntries.length === 0"
    class="workflow-notification-editor__empty"
  >
    No recipients configured for this event.
  </p>

  <div
    *ngIf="ruleEntries.length > 0"
    class="workflow-notification-editor__list"
  >
    <button
      uiButton
      [wrap]="true"
      *ngFor="let entry of ruleEntries; trackBy: trackRule"
      #notificationRuleButton
      type="button"
      class="workflow-notification-editor__rule"
      [class.workflow-notification-editor__rule--selected]="
        entry.index === selectedIndex()
      "
      [class.workflow-notification-editor__rule--invalid]="entry.rule === null"
      [disabled]="interactionBlocked"
      [attr.aria-pressed]="entry.index === selectedIndex()"
      (click)="selectRule(entry.index)"
    >
      <ng-container *ngIf="entry.rule as rule; else invalidRule">
        <span>
          <strong>{{ recipientLabel(rule) }}</strong>
          <small>{{ severityLabel(rule) }}</small>
        </span>
        <span class="workflow-notification-editor__rule-action">
          {{
            inspectionMode || !canAuthor ? "View recipient" : "Edit recipient"
          }}
          <i class="pi pi-chevron-right" aria-hidden="true"></i>
        </span>
      </ng-container>
      <ng-template #invalidRule>
        <span>
          <strong>Invalid recipient entry</strong>
          <small>
            {{
              inspectionMode
                ? "Preserved as incomplete saved evidence."
                : "Remove this entry to repair the draft."
            }}
          </small>
        </span>
      </ng-template>
    </button>
  </div>

  <details
    *ngIf="unavailableRecipientTypes.length > 0 && !referenceLoading"
    class="workflow-notification-editor__availability"
  >
    <summary>Some recipient types are unavailable</summary>
    <ul>
      <li *ngFor="let unavailable of unavailableRecipientTypes">
        <strong>{{ unavailable.label }}:</strong> {{ unavailable.reason }}
      </li>
    </ul>
    <p>
      Only approved targets can be added. Existing unavailable values remain
      visible for inspection.
    </p>
  </details>

  <div *ngIf="editorOpen" class="workflow-notification-editor__form">
    <h6
      #notificationFormHeading
      [id]="instanceId + '-form-title'"
      tabindex="-1"
    >
      {{
        isAdding()
          ? "Add recipient"
          : inspectionMode || !canAuthor
            ? "Recipient details"
            : "Edit recipient"
      }}
    </h6>

    <p
      *ngIf="localError() as error"
      class="workflow-notification-editor__error"
      role="alert"
    >
      {{ error }}
    </p>

    <ng-container *ngIf="isAdding() || selectedRule; else invalidSelection">
      <ui-form
        *ngFor="
          let renderVersion of formRenderVersions;
          trackBy: trackFormRenderVersion
        "
        #notificationForm
        [instanceId]="instanceId + '-form-' + renderVersion"
        aria-label="Recipient details"
        [config]="formConfig"
        [formValues]="formValues"
        [disabled]="
          !canAuthor || inspectionMode || interactionBlocked || referenceLoading
        "
        (formValuesChange)="onFormChange($event)"
      ></ui-form>

      <section
        class="workflow-notification-editor__content-preview"
        [attr.aria-labelledby]="instanceId + '-content-title'"
      >
        <h6 [id]="instanceId + '-content-title'">Recipient summary</h6>
        <dl>
          <div>
            <dt>Trigger</dt>
            <dd>{{ triggerLabel }}</dd>
          </div>
          <div>
            <dt>Who</dt>
            <dd>{{ recipientSummary }}</dd>
          </div>
          <div>
            <dt>Urgency</dt>
            <dd>{{ urgencySummary }}</dd>
          </div>
        </dl>
      </section>

      <p
        *ngIf="targetCatalogUnavailable"
        class="workflow-notification-editor__configuration"
        role="status"
      >
        {{ selectedTargetUnavailableReason }}
        Choose an available recipient type to apply a change.
      </p>

      <p
        *ngIf="formDirty()"
        class="workflow-notification-editor__pending"
        role="status"
      >
        This recipient has pending changes. Add or Apply updates it locally;
        Save draft includes valid pending changes. Apply or discard them before
        adding another recipient.
      </p>
      <p
        *ngIf="!formDirty() && canAuthor && !inspectionMode"
        class="workflow-notification-editor__local-state"
      >
        This recipient is in the local draft. Save draft persists the workflow
        changes.
      </p>
    </ng-container>

    <ng-template #invalidSelection>
      <p class="workflow-notification-editor__configuration" role="status">
        {{
          inspectionMode
            ? "This saved entry has no usable rule details. It remains visible as immutable evidence."
            : "This saved entry has no usable rule details. Remove it to repair the draft."
        }}
      </p>
    </ng-template>

    <div
      *ngIf="canAuthor && !inspectionMode"
      class="workflow-notification-editor__actions"
    >
      <button
        uiButton
        severity="primary"
        [outlined]="false"
        *ngIf="isAdding() || selectedRule"
        type="button"
        class="workflow-notification-editor__apply"
        [disabled]="
          interactionBlocked ||
          referenceLoading ||
          (!isAdding() && !formDirty())
        "
        (click)="applyRule()"
      >
        {{ isAdding() ? "Add recipient" : "Apply recipient" }}
      </button>
      <button
        uiButton
        *ngIf="isAdding() || selectedRule"
        type="button"
        [disabled]="interactionBlocked || referenceLoading"
        (click)="cancelEdit()"
      >
        {{ isAdding() ? "Cancel" : "Discard recipient changes" }}
      </button>
      <button
        uiButton
        severity="danger"
        [text]="true"
        [outlined]="false"
        *ngIf="!isAdding() && selectedIndex() !== null"
        type="button"
        [disabled]="interactionBlocked || referenceLoading || formDirty()"
        (click)="removeSelected()"
      >
        Remove recipient
      </button>
    </div>

    <button
      uiButton
      *ngIf="!isAdding()"
      type="button"
      class="workflow-notification-editor__done"
      [disabled]="interactionBlocked || referenceLoading || formDirty()"
      (click)="finishEdit()"
    >
      {{ inspectionMode || !canAuthor ? "Back to recipients" : "Done" }}
    </button>
  </div>

  <aside
    *ngIf="!embeddedContext"
    class="workflow-notification-editor__boundary"
  >
    <strong>Delivery not connected</strong>
    <p>
      Recipients are configured with the workflow version. No messages are sent
      until workflow event delivery and approved destinations are connected.
    </p>
  </aside>
</section>
